// UNIT series catalogue. UNIT is the whole ecosystem; UGAIN serves all of it.
// CITIES is only the first launch series, not the ecosystem.
//
// The catalogue is DATA, not code: screens read `data.series` (demo seed or,
// in api mode, the server bootstrap) and never hard-code a series name.
//
// Source of the default catalogue: the P2020 UNIT Ecosystem Complete Collection
// v2 (LIBRARY/P2020-LIB-UNIT-ECOSYSTEM/P2020-ClaudeDesign/ECOSYSTEM/
// UNIT-EcosystemCompleteCollection-v2.html), 20 series × 100 coins, in its order.
// Ids are the singular series name, as stored on activities and profiles.

import type { PhotoId } from '../data/photos';

export interface SeriesDef {
  /** Stable id stored on activities, ambassadors and affiliations, e.g. "City". */
  id: string;
  /** Display label in UGAIN, e.g. "City UNITS". */
  label: string;
  /** Name in the ecosystem reference, e.g. "City Coins". */
  referenceName: string;
  /** Position in the ecosystem reference (1 = first). */
  order: number;
  /** "launch" = the current launch series. Everything else is listed but not yet launched. */
  status: 'launch' | 'upcoming';
  /** Optional tile art (demo seed only). */
  art?: PhotoId;
}

const NAMES = [
  'City', 'Country', 'Name', 'Birthday', 'University', 'School', 'Pastime', 'Food', 'Art', 'Book',
  'Impact', 'Industry', 'Investor', 'Politician', 'Scientist', 'Influencer', 'Music', 'Sport', 'Event', 'Film',
] as const;

/** Only the first launch series is marked live; the reference does not launch the others yet. */
const LAUNCH: readonly string[] = ['City'];

export const ECOSYSTEM_SERIES: readonly SeriesDef[] = NAMES.map((id, i) => ({
  id,
  label: `${id} UNITS`,
  referenceName: `${id} Coins`,
  order: i + 1,
  status: LAUNCH.includes(id) ? 'launch' : 'upcoming',
}));

/** Label for a series id; unknown ids still render (the catalogue may be newer than the client). */
export function seriesLabel(catalogue: readonly SeriesDef[], id: string): string {
  return catalogue.find((s) => s.id === id)?.label ?? `${id} UNITS`;
}

/** Catalogue in display order: launch series first, then reference order. */
export function orderedSeries(catalogue: readonly SeriesDef[]): SeriesDef[] {
  return [...catalogue].sort((a, b) => (a.status === b.status ? a.order - b.order : a.status === 'launch' ? -1 : 1));
}
